// Frame-driven host/coverage/pause state for DeletionNotices. Plain object +
// subscribe/getSnapshot so the component reads it with useSyncExternalStore
// and never touches refs or sets state from an effect during render.
import {
  firstFocusableOutside,
  hostIsCovered,
  resolveNoticeHost,
  sourceIsSettled,
  type NoticeSource,
} from './notice-host';

export interface UndoNoticeInput {
  /** Bumped by PrayPage every time Undo is (re)offered. */
  event: number;
  source: NoticeSource | null;
}

export interface ProtectionNoticeInput {
  /** Bumped by PrayPage every time a protection message is shown. */
  event: number;
  message: string;
  source: NoticeSource | null;
}

/**
 * Two independent protection-message channels (2026-10-15 amendment):
 * `info` (pending / preparing, PrayPage expires it after 6 s) and `failure`
 * (failed delete/restore, keepsake problems; stays until dismissed or until
 * its own successful retry). Each resolves its host separately.
 */
export type ProtectionChannel = 'info' | 'failure';

export interface NoticeInputs {
  home: { readonly current: HTMLElement | null };
  undo: UndoNoticeInput | null;
  info: ProtectionNoticeInput | null;
  failure: ProtectionNoticeInput | null;
  onUndoPausedChange: (paused: boolean) => void;
}

export type PauseReason = 'hover' | 'focus' | 'covered';

export interface NoticeSnapshot {
  undoHost: HTMLElement | null;
  infoHost: HTMLElement | null;
  failureHost: HTMLElement | null;
  undoReady: boolean;
  infoReady: boolean;
  failureReady: boolean;
  /** Active Undo pause reasons (empty when running or when no Undo). */
  reasons: readonly PauseReason[];
  home: HTMLElement | null;
}

// A deleting dialog normally unmounts within a few frames of its close. If it
// never does, show the notice anyway rather than hide it for good.
const MAX_WAIT_FRAMES = 240;
// After a NavStack change, keep re-checking for a few frames so React's commit
// and the coordinator's history echo have both landed.
const SETTLE_FRAMES = 3;

const CHANNELS: readonly ProtectionChannel[] = ['info', 'failure'];
const HOST_KEY = { info: 'infoHost', failure: 'failureHost' } as const;
const READY_KEY = { info: 'infoReady', failure: 'failureReady' } as const;

function focusIsLost(): boolean {
  const active = document.activeElement;
  return !active || active === document.body || !active.isConnected;
}

export class NoticeController {
  private inputs: NoticeInputs | null = null;
  private undoEvent: number | null = null;
  private undoSource: NoticeSource | null = null;
  private channelEvent: Record<ProtectionChannel, number | null> = { info: null, failure: null };
  private channelSource: Record<ProtectionChannel, NoticeSource | null> = { info: null, failure: null };
  private waited = 0;
  private settle = 0;
  private frame: number | null = null;
  private readyFrames: number[] = [];
  private hovered = false;
  private focused = false;
  private covered = true;
  private reportedPaused = false;
  private listeners = new Set<() => void>();
  private snapshot: NoticeSnapshot = {
    undoHost: null,
    infoHost: null,
    failureHost: null,
    undoReady: false,
    infoReady: false,
    failureReady: false,
    reasons: [],
    home: null,
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): NoticeSnapshot => this.snapshot;

  setInputs(inputs: NoticeInputs): void {
    this.inputs = inputs;
    const undoEvent = inputs.undo?.event ?? null;
    let changed = false;
    if (undoEvent !== this.undoEvent) {
      this.undoEvent = undoEvent;
      this.undoSource = inputs.undo?.source ?? null;
      this.waited = 0;
      this.hovered = false;
      this.focused = false;
      this.patch({ undoHost: null, undoReady: false });
      changed = true;
    }
    for (const channel of CHANNELS) {
      const event = inputs[channel]?.event ?? null;
      if (event === this.channelEvent[channel]) continue;
      this.channelEvent[channel] = event;
      this.channelSource[channel] = inputs[channel]?.source ?? null;
      this.waited = 0;
      this.patch({ [HOST_KEY[channel]]: null, [READY_KEY[channel]]: false });
      changed = true;
    }
    if (inputs.home.current !== this.snapshot.home) this.patch({ home: inputs.home.current });
    this.updatePause();
    if (changed) this.schedule();
  }

  /** Any NavStack change can cover, uncover, or remove a host. */
  layersChanged = (): void => {
    this.schedule();
  };

  setHovered(hovered: boolean): void {
    if (this.hovered === hovered) return;
    this.hovered = hovered;
    this.updatePause();
  }

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    this.focused = focused;
    this.updatePause();
  }

  /**
   * The focused Undo control is leaving the page (Undo tapped, timer, or a
   * latest-wins re-host). Recover only if focus really fell to <body> and the
   * old host is still the topmost surface, so a newer dialog keeps its focus.
   */
  focusedUndoRemoved(previousHost: HTMLElement): void {
    this.focused = false;
    this.focusedNoticeRemoved(previousHost);
    this.updatePause();
  }

  /**
   * Any focused notice control left the page (Undo, or a failure's Dismiss).
   * Same rule: only when focus fell to <body> and the host is still topmost.
   */
  focusedNoticeRemoved(previousHost: HTMLElement): void {
    requestAnimationFrame(() => {
      if (!focusIsLost()) return;
      const current = this.inputs?.undo ? this.snapshot.undoHost : null;
      if (current && current.isConnected && !hostIsCovered(current)) {
        const button = current.querySelector<HTMLElement>('[data-notice="undo"] button');
        if (button) {
          button.focus({ preventScroll: true });
          return;
        }
      }
      if (!previousHost.isConnected || hostIsCovered(previousHost)) return;
      firstFocusableOutside(previousHost)?.focus({ preventScroll: true });
    });
  }

  dispose(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    for (const frame of this.readyFrames) cancelAnimationFrame(frame);
    this.frame = null;
    this.readyFrames = [];
    this.listeners.clear();
  }

  private schedule(): void {
    this.settle = Math.max(this.settle, SETTLE_FRAMES);
    if (this.frame === null) this.frame = requestAnimationFrame(this.evaluate);
  }

  private evaluate = (): void => {
    this.frame = null;
    const inputs = this.inputs;
    if (!inputs) return;
    const home = inputs.home.current;
    let pending = false;

    const pick = (
      present: boolean,
      host: HTMLElement | null,
      source: NoticeSource | null,
    ): { host: HTMLElement | null; source: NoticeSource | null } => {
      if (!present) return { host: null, source: null };
      if (host && host.isConnected) return { host, source: null };
      // Wait for the deleting dialogs to unmount; a host that closed instead
      // re-resolves to whatever is now on top.
      if (!sourceIsSettled(source) && this.waited < MAX_WAIT_FRAMES) {
        pending = true;
        return { host: null, source };
      }
      return { host: resolveNoticeHost(home), source: null };
    };

    const undo = pick(inputs.undo !== null, this.snapshot.undoHost, this.undoSource);
    const channels = CHANNELS.map((channel) => {
      const picked = pick(inputs[channel] !== null, this.snapshot[HOST_KEY[channel]], this.channelSource[channel]);
      this.channelSource[channel] = picked.source;
      return { channel, host: picked.host };
    });
    this.undoSource = undo.source;
    this.waited = pending ? this.waited + 1 : 0;

    if (undo.host !== this.snapshot.undoHost) {
      // A re-hosted pill is a new element: hover/focus start clean, and its
      // text is inserted one frame after the region attaches so screen
      // readers announce it.
      this.hovered = false;
      this.focused = false;
      this.patch({ undoHost: undo.host, undoReady: false });
      if (undo.host) this.markReady('undoReady');
    }
    for (const { channel, host } of channels) {
      if (host === this.snapshot[HOST_KEY[channel]]) continue;
      this.patch({ [HOST_KEY[channel]]: host, [READY_KEY[channel]]: false });
      if (host) this.markReady(READY_KEY[channel]);
    }
    if (home !== this.snapshot.home) this.patch({ home });
    this.covered = !undo.host || hostIsCovered(undo.host);
    this.updatePause();

    if (pending || this.settle > 0) {
      if (this.settle > 0) this.settle -= 1;
      this.frame = requestAnimationFrame(this.evaluate);
    }
  };

  private markReady(key: 'undoReady' | 'infoReady' | 'failureReady'): void {
    const frame = requestAnimationFrame(() => {
      this.readyFrames = this.readyFrames.filter((pendingFrame) => pendingFrame !== frame);
      this.patch({ [key]: true });
    });
    this.readyFrames.push(frame);
  }

  private updatePause(): void {
    const undoPresent = this.inputs?.undo != null;
    const reasons: PauseReason[] = [];
    if (undoPresent) {
      if (this.hovered) reasons.push('hover');
      if (this.focused) reasons.push('focus');
      if (this.covered || !this.snapshot.undoHost) reasons.push('covered');
    }
    const same =
      reasons.length === this.snapshot.reasons.length &&
      reasons.every((reason, index) => reason === this.snapshot.reasons[index]);
    if (!same) this.patch({ reasons });
    const paused = reasons.length > 0;
    if (paused !== this.reportedPaused) {
      this.reportedPaused = paused;
      this.inputs?.onUndoPausedChange(paused);
    }
  }

  private patch(next: Partial<NoticeSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...next };
    for (const listener of this.listeners) listener();
  }
}
